/**
 * El catálogo de venta de un cliente: UN componente, DOS puertas.
 *
 * Acá vive lo que el diálogo de Venta rápida ya hacía bien y el formulario de
 * Ventas no hacía: mostrar todo lo que la empresa vende **con el precio que le
 * corresponde a ESTE cliente**, el precio de lista tachado al lado, la regla que
 * explica la diferencia («Lista Mayorista: -10 %») y cada cuánto vence.
 *
 * Se extrajo del diálogo sin cambiarle una conducta, porque el formulario de
 * Ventas necesitaba exactamente lo mismo y reimplementarlo habría dejado dos
 * selectores que muestran dos precios distintos el día que uno se quede viejo.
 * Las dos puertas son:
 *
 *  1. `VentaRapidaDialog` — marcar varios productos de una y anotarlos juntos.
 *  2. `ProductoVendidoField` — el widget `catalogo` del campo «Qué se le
 *     vendió» en el formulario de una venta: se elige UNO.
 *
 * De dónde salen los datos
 * ------------------------
 * De la acción de servidor `base.partner.catalogo_venta_rapida`. Acá **no se
 * calcula ningún precio ni ninguna fecha**: mostrar un número calculado del lado
 * del cliente sería prometer un importe que la factura no va a repetir. La
 * acción resuelve cada precio con `products.services.pricing.resolver_precio`
 * —el único motor de precios del ERP— y ya filtra por `active`, `can_be_sold` y
 * empresa, que es el dominio que al `RelationPicker` genérico le falta.
 *
 * Y se le pide **a una fecha**: la de la venta que se está cargando. El precio y
 * el vencimiento dependen de ella, así que preguntar sin fecha es preguntar por
 * hoy y contestar mal en cuanto la venta es de otro día.
 *
 * La plata no pasa por `number`
 * -----------------------------
 * Las cantidades y los precios se guardan como TEXTO y se suman con enteros de
 * centavos: un `number` de JavaScript no puede representar 12,30 y en un precio
 * eso son centavos que no cierran. Es la misma regla que el backend aplica del
 * otro lado.
 */

import { useQuery } from '@tanstack/react-query';

import { formatCurrency, formatDate } from '@/components/fields/format';
import { parsearNumero } from '@/components/fields/parse-numero';
import { Badge } from '@/components/shell/primitives';
import { api } from '@/lib/api';
import type { ActionResult } from '@/lib/types';

/** El modelo que publica la acción del catálogo. Es el contacto, no el producto. */
export const MODELO_CATALOGO = 'base.partner';

/** Una fila del catálogo, tal como la serializa `quick_sale.ProductoOfrecido`. */
export interface ProductoOfrecido {
  product_id: number;
  name: string;
  code: string;
  category: string;
  uom: string;
  base_price: string;
  price: string;
  price_rule: string;
  expiry_label: string;
  expiry_date: string | null;
  renewal_period: string;
  is_recurring: boolean;
}

/** El catálogo del cliente (`quick_sale.CatalogoDeVenta`). */
export interface CatalogoDeVenta {
  partner_id: number;
  partner_name: string;
  pricelist_name: string;
  currency: string;
  on_date: string;
  products: ProductoOfrecido[];
  truncated: boolean;
}

/** Lee el `data` del primer resultado de una acción, con desconfianza. */
export function datosDe<T>(resultado: ActionResult | undefined): T | null {
  const resultados = (resultado as { results?: unknown } | undefined)?.results;
  if (!Array.isArray(resultados) || resultados.length === 0) return null;
  const datos = (resultados[0] as { data?: unknown }).data;
  if (datos === null || typeof datos !== 'object') return null;
  return datos as T;
}

/** Una fecha del formulario lista para viajar, o `null` si no hay ninguna. */
export function fechaDeVenta(valor: unknown): string | null {
  if (typeof valor !== 'string') return null;
  const texto = valor.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(texto) ? texto : null;
}

/**
 * El catálogo de un cliente **a una fecha**, cacheado por cliente y por fecha.
 *
 * La clave incluye el `partnerId` porque el catálogo YA viene resuelto para ese
 * cliente del lado del servidor: dos clientes con listas distintas son dos
 * entradas distintas. Y incluye la fecha porque el precio y el vencimiento
 * **dependen de ella**: el vencimiento se cuenta desde el día de la venta y el
 * precio sale de la lista que regía ese día.
 *
 * Sin fecha el servidor contesta a hoy, que es lo que corresponde a la venta
 * rápida —se está vendiendo ahora—. El formulario de Ventas sí la manda: ahí se
 * carga una venta que puede ser de ayer o del mes pasado, y con "hoy" clavado
 * «Llamar el» quedaba mal en toda venta que no se cargara el mismo día.
 */
export function useCatalogoDeVenta(partnerId: number | null, fecha?: string | null) {
  const alDia = fechaDeVenta(fecha);
  return useQuery({
    queryKey: ['venta-rapida-catalogo', partnerId, alDia ?? 'hoy'],
    enabled: partnerId !== null,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const respuesta = await api.callAction(
        MODELO_CATALOGO,
        partnerId as number,
        'catalogo_venta_rapida',
        alDia === null ? {} : { fecha: alDia },
      );
      return datosDe<CatalogoDeVenta>(respuesta);
    },
  });
}

/** Normaliza para buscar: sin acentos, en minúsculas. */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** Filtra el catálogo por nombre, código o rubro. Vacío devuelve todo. */
export function filtrarCatalogo(
  productos: ProductoOfrecido[],
  busqueda: string,
): ProductoOfrecido[] {
  const aguja = normalizar(busqueda.trim());
  if (aguja === '') return productos;
  return productos.filter((p) => normalizar(`${p.name} ${p.code} ${p.category}`).includes(aguja));
}

// ---------------------------------------------------------------------------
// Plata en centavos: sumar sin `float`
// ---------------------------------------------------------------------------
/**
 * Convierte un decimal escrito a centavos enteros.
 *
 * Sumar importes con `Number` da 0.30000000000000004 en cuanto hay tres
 * renglones, y ese es el total que el vendedor le lee al cliente por teléfono.
 * Con centavos enteros la suma es exacta y el redondeo ocurre una sola vez, acá,
 * con la misma regla que usa el backend (media unidad para arriba).
 *
 * Devuelve `null` si el texto no es un número: quien lo llama decide si eso es
 * un cero o un error visible.
 */
export function aCentavos(texto: string): number | null {
  const limpio = parsearNumero((texto ?? '').trim());
  if (limpio === '') return null;
  const numero = Number(limpio);
  if (!Number.isFinite(numero)) return null;
  return Math.round(numero * 100);
}

/** El total de un renglón en centavos: cantidad × precio, sin `float` acumulado. */
export function subtotalEnCentavos(cantidad: string, precio: string): number | null {
  const q = Number(parsearNumero((cantidad ?? '').trim()));
  const p = aCentavos(precio);
  if (p === null || !Number.isFinite(q)) return null;
  return Math.round(q * p);
}

/** De centavos enteros al texto decimal que espera la API (`"1234.56"`). */
export function deCentavos(centavos: number): string {
  const signo = centavos < 0 ? '-' : '';
  const abs = Math.abs(centavos);
  return `${signo}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * El importe de una venta: precio × cantidad, como texto decimal.
 *
 * Devuelve `null` si alguno de los dos no es un número, para que quien lo llama
 * no escriba un importe inventado en el formulario.
 */
export function importeDe(precio: string, cantidad: string): string | null {
  const centavos = subtotalEnCentavos(cantidad === '' ? '1' : cantidad, precio);
  return centavos === null ? null : deCentavos(centavos);
}

// ---------------------------------------------------------------------------
// La ficha de un producto en el catálogo
// ---------------------------------------------------------------------------
export interface FichaDelProductoProps {
  producto: ProductoOfrecido;
  /** Id del elemento clickeable al que la ficha sirve de etiqueta. */
  htmlFor?: string;
}

/**
 * Lo que se ve de un producto en el selector, en las dos puertas.
 *
 * Cinco datos y ni uno más: **nombre**, **código**, **precio de este cliente**,
 * **precio de lista tachado** cuando no coinciden y **cada cuánto vence** (con
 * la fecha concreta a la que vencería, contada desde el día de la venta).
 * Debajo, la regla que explica el precio, si hay alguna.
 *
 * El precio de lista tachado no es decoración: es lo que hace que quien pisa el
 * precio a mano sepa de qué se está apartando.
 */
export function FichaDelProducto({ producto, htmlFor }: FichaDelProductoProps) {
  const encabezado = (
    <span className="flex flex-wrap items-baseline gap-x-2">
      <span className="font-medium">{producto.name}</span>
      {producto.code !== '' && <span className="text-xs text-fg-faint">[{producto.code}]</span>}
    </span>
  );

  return (
    <>
      {htmlFor === undefined ? (
        encabezado
      ) : (
        <label htmlFor={htmlFor} className="flex cursor-pointer">
          {encabezado}
        </label>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-semibold tabular-nums">
          {formatCurrency(producto.price, { minimumFractionDigits: 2 })}
        </span>
        {producto.price !== producto.base_price && (
          <span className="text-xs text-fg-faint line-through tabular-nums">
            {formatCurrency(producto.base_price, { minimumFractionDigits: 2 })}
          </span>
        )}
        <Badge tone={producto.is_recurring ? 'warning' : 'neutral'}>{producto.expiry_label}</Badge>
        {producto.expiry_date !== null && (
          <span className="text-xs text-fg-faint">
            Vencería el {formatDate(producto.expiry_date)}
          </span>
        )}
      </div>
      {producto.price_rule !== '' && (
        <p className="mt-0.5 text-xs text-fg-faint">{producto.price_rule}</p>
      )}
    </>
  );
}

/**
 * El aviso de catálogo truncado, igual en las dos puertas.
 *
 * Un selector que calla que le faltan productos es peor que uno que no los
 * tiene: quien no encuentra el trámite piensa que no está cargado y lo escribe
 * a mano, que es justo lo que este trabajo viene a evitar.
 */
export function AvisoDeCatalogoTruncado() {
  return (
    <p className="border-t border-line px-5 py-3 text-xs text-fg-faint">
      El catálogo es más grande que lo que entra en este selector. Para una venta con muchos
      renglones, armá un presupuesto en Ventas ▸ Presupuestos.
    </p>
  );
}
